import { CompartmentSelector } from '@ekkogmbh/apisdk';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import CompartmentSelectorConfigurationField from './CompartmentSelectorConfigurationField';
import { dataTableHighlight } from 'src/Common/Components/DataTable';

interface CompartmentSelectorChipProps {
  compartmentSelector: Partial<CompartmentSelector>;
  highlightText?: string;
}

export default function CompartmentSelectorChip({
  compartmentSelector,
  highlightText,
}: CompartmentSelectorChipProps): React.JSX.Element {
  const chipContent = useMemo(
    () => (
      <Stack direction={'column'} margin={1} marginBottom={2}>
        <Typography fontWeight={600} variant={'body1'}>
          {dataTableHighlight(compartmentSelector?.name ?? '', highlightText)}
        </Typography>
        <Typography variant={'body2'}>{'@' + compartmentSelector?.coordinate}</Typography>
      </Stack>
    ),
    [compartmentSelector?.name, compartmentSelector?.coordinate, highlightText],
  );

  const tooltipContent: React.JSX.Element | undefined = useMemo(() => {
    if ([compartmentSelector?.type, compartmentSelector?.configuration].includes(undefined)) {
      return undefined;
    }

    return (
      <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 200 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing={1}
          color="primary.main"
          textAlign="center"
        >
          {compartmentSelector.type as string}
        </Typography>
        <Divider />
        <CompartmentSelectorConfigurationField configuration={compartmentSelector.configuration!} />
      </Stack>
    );
  }, [compartmentSelector]);

  return (
    <TooltipHardened arrow tooltip={tooltipContent}>
      <Chip
        variant={'outlined'}
        style={{
          position: 'relative',
          margin: 8,
          height: 48,
          width: 'fit-content',
          overflow: 'hidden',
          flexShrink: 0,
        }}
        label={chipContent}
      />
    </TooltipHardened>
  );
}
